import React, { PropsWithChildren, useEffect, useState } from 'react'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import { useI18nSelector } from 'v3/context/i18n'
import { parse } from 'query-string'
import Button from '../Button/Button'
import './OneClick.scss'
import { Lead } from '../../types'
import config from '../../config'
import { doRequest } from '../../middleware'
import { Loading } from '../Loading/Loading'
import { trackEvent } from '../../util'
import { useRecaptcha, useRecaptchaState } from '../../v3/context/recaptcha'

type OneClickProps = {
  leadID: string
  result: string | undefined
}

type OneClickParams = Partial<Lead> & {
  auth_token: string
  recaptcha_response: string | null
}

declare global {
  interface Window {
    grecaptcha: {
      enterprise: any
    }
  }
}

export const OneClickAction = () => {
  const history = useHistory()
  const location = useLocation()
  const i18n = useI18nSelector()
  const recaptcha = useRecaptcha()
  const { leadID, result } = useParams<OneClickProps>()

  // Letting params be undefined without search prevents the recaptcha
  // submission from duplicating on redirects
  let params: OneClickParams | undefined
  if (location.search) {
    params = parse(location.search)
  }

  const success = result === 'success'
  const [loading, setLoading] = useState<boolean>(!result)

  useEffect(() => {
    if (!recaptcha || !leadID || !params || !loading) return
    recaptcha.enterprise.ready(async () => {
      try {
        const token = await recaptcha.enterprise.execute(config.recaptchaKey, { action: 'LeadOCA' })
        await doRequest({
          url: `${config.v3Api}/lead/${leadID}/oneclick`,
          method: 'POST',
          payload: {
            ...params,
            recaptcha_response: token,
          },
          headers: {
            'Content-Type': 'application/json',
          },
          type: 'OCA_LEAD_UPDATE',
        })
        trackEvent('leadOCASuccess', { ocaMeta: { leadID: leadID, ...params } })
        history.replace(`/oca/lead/${leadID}/success`)
      } catch (err) {
        trackEvent('leadOCAError', { ocaMeta: { leadID: leadID, ...params } })
        history.replace(`/oca/lead/${leadID}/error`)
      } finally {
        setLoading(false)
      }
    })
  }, [history, leadID, loading, params, recaptcha])

  switch (loading) {
    case true:
      return (
        <Content>
          <Loading title={i18n('leads.oca.loading.title')} message={null} />
        </Content>
      )
    default:
      // ?from param is for clickstream tracking
      let path = `/v3/leads/${leadID}?from=ocaError`
      let message = i18n('leads.oca.error.message')
      if (success) {
        path = `/v3/leads/${leadID}?from=ocaSuccess`
        message = i18n('leads.oca.success.message')
      }
      return (
        <Content>
          <span className="message">{message}</span>
          <div className="buttons">
            <Button className="view-lead" onClick={() => history.push(path)} variant="contained" color="primary" disableElevation>
              {i18n('leads.oca.button.label')}
            </Button>
          </div>
        </Content>
      )
  }
}

const Content = ({ children }: PropsWithChildren<any>) => {
  return (
    <div className="OneClick">
      <div className="Content">
        <div className="form">
          <div className="logo" />
          {children}
        </div>
      </div>
    </div>
  )
}
